import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthStore } from '@/stores/authStore';
import { hasPermission, type Permission } from '@/lib/rbac';
import { adminCopy } from '@/lib/copy';
import { cn } from '@/lib/utils';

export interface PhiFieldProps {
  label: string;
  value: ReactNode;
  permission?: Permission;
  width?: 'auto' | 'sm' | 'md' | 'lg';
  inline?: boolean;
  className?: string;
}

const WIDTH_MAP: Record<NonNullable<PhiFieldProps['width']>, string> = {
  auto: '',
  sm: 'w-32',
  md: 'w-48',
  lg: 'w-72',
};

function usePhiAccess(permission: Permission): boolean {
  const user = useAuthStore((s) => s.user);
  return user ? hasPermission(user.role, permission) : false;
}

export function PhiField({
  label,
  value,
  permission = 'client:read_phi' as Permission,
  width = 'auto',
  inline,
  className,
}: PhiFieldProps) {
  const allowed = usePhiAccess(permission);

  const maskedPill = (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={`${label}, protected health information, access required`}
            className={cn(
              'text-body-md inline-flex cursor-help items-center gap-2 rounded-sm bg-neutral-100 px-3 py-1.5 text-neutral-500',
              WIDTH_MAP[width],
            )}
          >
            <Lock className="h-4 w-4 text-neutral-400" strokeWidth={1.5} aria-hidden="true" />
            <span>••• ••• ••</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-meta-sm">
          {adminCopy.phi.tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (inline) {
    return allowed ? <>{value}</> : maskedPill;
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="text-label-md text-accent-ink tracking-label uppercase">{label}</span>
      {allowed ? <div className="text-body-md text-neutral-900">{value}</div> : maskedPill}
    </div>
  );
}

export interface PhiSectionProps {
  children: ReactNode;
  allRedacted?: boolean;
  className?: string;
}

export function PhiSection({ children, allRedacted, className }: PhiSectionProps) {
  if (allRedacted) {
    return (
      <div
        className={cn(
          'text-meta-sm flex items-center gap-2 rounded-md bg-neutral-100 px-3 py-2 text-neutral-500',
          className,
        )}
      >
        <Lock className="h-4 w-4 text-neutral-400" strokeWidth={1.5} aria-hidden="true" />
        {adminCopy.phi.sectionAllRedacted}
      </div>
    );
  }
  return <>{children}</>;
}
